import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormattedMessage, useIntl } from "react-intl";
import { Attendee } from "../../redux/types/types";
import { getAttendeeImportantInfoApi } from "../../utils/api/attendees";

interface AttendeeImportantInfoButtonProps {
  attendee: Pick<
    Attendee,
    "id" | "firstName" | "lastName" | "hasImportantInfo"
  >;
  size?: "small" | "medium" | "large";
}

export default function AttendeeImportantInfoButton({
  attendee,
  size = "small",
}: AttendeeImportantInfoButtonProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importantInfo, setImportantInfo] = useState<string | null>(null);
  const [importantInfoLoaded, setImportantInfoLoaded] = useState(false);
  const [visible, setVisible] = useState(attendee.hasImportantInfo === true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (attendee.hasImportantInfo !== undefined) {
      setVisible(attendee.hasImportantInfo);
      setImportantInfoLoaded(false);
      return () => {
        cancelled = true;
      };
    }

    getAttendeeImportantInfoApi(attendee.id)
      .then((response) => {
        if (cancelled) return;
        const value = response.data.importantInfo;
        setImportantInfo(value);
        setImportantInfoLoaded(true);
        setVisible(Boolean(value?.trim()));
      })
      .catch(() => {
        if (!cancelled) setVisible(false);
      });

    return () => {
      cancelled = true;
    };
  }, [attendee.hasImportantInfo, attendee.id]);

  const handleOpen = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
    if (importantInfoLoaded) return;

    setLoading(true);
    setError(false);

    try {
      const response = await getAttendeeImportantInfoApi(attendee.id);
      setImportantInfo(response.data.importantInfo);
      setImportantInfoLoaded(true);
    } catch {
      setError(true);
      setImportantInfo(null);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <Button
        variant="text"
        color="info"
        size={size}
        startIcon={<InfoOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
        onClick={handleOpen}
        aria-label={intl.formatMessage({ id: "attendee.importantInfo.view" })}
        sx={{
          minWidth: 0,
          maxWidth: "100%",
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: "action.hover",
          alignSelf: "flex-start",
          textTransform: "none",
          fontSize: "0.75rem",
          fontWeight: 600,
          lineHeight: 1.25,
          whiteSpace: "normal",
          textAlign: "left",
          verticalAlign: "middle",
          "& .MuiButton-startIcon": { ml: 0, mr: 0.5 },
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <FormattedMessage id="attendee.importantInfo.view" />
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogTitle>
          <FormattedMessage id="attendee.importantInfo.title" />
          {` — ${attendee.firstName} ${attendee.lastName}`}
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <CircularProgress size={24} />
          ) : error ? (
            <Alert severity="error">
              <FormattedMessage id="attendee.importantInfo.loadError" />
            </Alert>
          ) : (
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {importantInfo || (
                <FormattedMessage id="attendee.importantInfo.empty" />
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            <FormattedMessage id="common.close" defaultMessage="Close" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
